import { textToList } from '../../../components/common';
import type {
  Monitor,
  MonitorAlert,
  MonitorDraft,
  MonitorInput,
  MonitorProposal,
  MonitorProposalItem,
  MonitorProposedAction,
  MonitorScope,
  MonitorSource,
} from '../../api/monitors';
import { validateCron } from './cron';

/** Editor state: strings for every field so the inputs stay controlled; `toInput` converts and validates. */
export interface SourceForm {
  key: number;
  label: string;
  type: string;
  query: string;
  logSince: string;
  entityKeyColumns: string;
  instructions: string;
}

export interface MonitorForm {
  id?: string;
  scope: MonitorScope;
  tenantId: string;
  systemId: string;
  name: string;
  description: string;
  cronExpression: string;
  enabled: boolean;
  runMode: string;
  instructions: string;
  maxToolCalls: string;
  combineMode: string;
  gateInstructions: string;
  logSince: string;
  sources: SourceForm[];
  reportSpecText: string;
  notifyEmails: string;
  notifyMode: string;
  /** Guesses the generator made; shown at the top of the editor. */
  assumptions?: string[];
  /** A card or agent made from a chat answer: it replays a saved response and has no sources to edit. */
  fromSavedResponse?: boolean;
}

export const RUN_MODES = [
  { value: 'agent', label: 'Agent: investigate every new alert' },
  { value: 'report', label: 'Report: mail the rows, no model call' },
];
export const COMBINE_MODES = [
  { value: 'union', label: 'Union: every source alerts on its own' },
  { value: 'llm_gate', label: 'Gate: the model correlates the sources' },
];
export const NOTIFY_MODES = [
  { value: 'off', label: 'Off' },
  { value: 'new_alerts', label: 'New alerts: only when a run opens alerts' },
  { value: 'findings', label: 'Findings: every run with findings' },
];
export const SOURCE_TYPES = [
  { value: 'query', label: 'Literal query' },
  { value: 'gcp_logs', label: 'Cloud Logging' },
  { value: 'llm', label: 'Model check' },
];

const LABEL = /^[a-z0-9_-]+$/;
const LOOKBACK = /^\d+[mhd]$/;

let keySeq = 1;

export const emptySource = (): SourceForm => ({
  key: keySeq++,
  label: '',
  type: 'query',
  query: '',
  logSince: '',
  entityKeyColumns: '',
  instructions: '',
});

export const emptyForm = (
  scope: MonitorScope = 'system',
  ids: { tenantId?: string; systemId?: string } = {}
): MonitorForm => ({
  scope,
  tenantId: ids.tenantId ?? '',
  systemId: ids.systemId ?? '',
  name: '',
  description: '',
  cronExpression: '0 6 * * *',
  enabled: true,
  runMode: 'agent',
  instructions: '',
  maxToolCalls: '6',
  combineMode: 'union',
  gateInstructions: '',
  logSince: '',
  sources: [emptySource()],
  reportSpecText: '',
  notifyEmails: '',
  notifyMode: 'off',
});

const sourceToForm = (s: MonitorSource): SourceForm => ({
  key: keySeq++,
  label: s.label ?? '',
  type: s.type ?? 'query',
  query: s.query ?? '',
  logSince: s.logSince ?? '',
  entityKeyColumns: (s.entityKeyColumns ?? []).join(', '),
  instructions: s.instructions ?? '',
});

const json = (v: unknown) => (v ? JSON.stringify(v, null, 2) : '');

export function fromMonitor(m: Monitor): MonitorForm {
  return {
    id: m.id,
    scope: m.scope ?? (m.systemId ? 'system' : m.tenantId ? 'tenant' : 'global'),
    tenantId: m.tenantId ?? '',
    systemId: m.systemId ?? '',
    name: m.name ?? '',
    description: m.description ?? '',
    cronExpression: m.cronExpression ?? '',
    enabled: m.enabled ?? true,
    runMode: m.runMode ?? 'agent',
    instructions: m.instructions ?? '',
    maxToolCalls: String(m.maxToolCalls ?? 6),
    combineMode: m.combineMode ?? 'union',
    gateInstructions: m.gateInstructions ?? '',
    logSince: m.logSince ?? '',
    sources: (m.sources ?? []).map(sourceToForm),
    reportSpecText: json(m.reportSpec),
    notifyEmails: (m.notifyEmails ?? []).join(', '),
    notifyMode: m.notifyMode ?? 'off',
    fromSavedResponse: !!m.savedQueryId,
  };
}

/** A generated draft → editor form, scoped to the tenant or system it was generated for. */
export function fromDraft(d: MonitorDraft, ids: { systemId?: string; tenantId?: string }): MonitorForm {
  const scope: MonitorScope = ids.tenantId ? 'tenant' : ids.systemId ? 'system' : 'global';
  return {
    ...emptyForm(scope, ids),
    name: d.name ?? '',
    description: d.description ?? '',
    cronExpression: d.cronExpression ?? '0 6 * * *',
    enabled: d.enabled ?? true,
    runMode: d.runMode ?? 'agent',
    instructions: d.instructions ?? '',
    maxToolCalls: d.maxToolCalls != null ? String(d.maxToolCalls) : '6',
    combineMode: d.combineMode ?? 'union',
    gateInstructions: d.gateInstructions ?? '',
    logSince: d.logSince ?? '',
    sources: d.sources?.length ? d.sources.map(sourceToForm) : [emptySource()],
    reportSpecText: json(d.reportSpec),
    notifyEmails: (d.notifyEmails ?? []).join(', '),
    notifyMode: d.notifyMode ?? 'off',
    assumptions: d.assumptions,
  };
}

/** The request body, or the first reason the form cannot be sent. */
export function toInput(f: MonitorForm): { body: MonitorInput } | { error: string } {
  if (!f.name.trim()) return { error: 'Name is required.' };
  const cronError = validateCron(f.cronExpression);
  if (cronError) return { error: `Schedule: ${cronError}.` };
  if (f.scope === 'tenant' && !f.tenantId) return { error: 'Choose the tenant.' };
  if (f.scope === 'system' && !f.systemId) return { error: 'Choose the system.' };
  if (f.logSince.trim() && !LOOKBACK.test(f.logSince.trim()))
    return { error: 'Log lookback must look like 30m, 2h or 1d.' };
  if (!f.fromSavedResponse) {
    if (!f.sources.length) return { error: 'At least one source is required.' };
    const seen = new Set<string>();
    for (const s of f.sources) {
      const label = s.label.trim();
      if (!label) return { error: 'Every source needs a label.' };
      if (!LABEL.test(label)) return { error: `Source label "${label}" may only use a-z, 0-9, _ and -.` };
      if (seen.has(label)) return { error: `Source label "${label}" is used twice.` };
      seen.add(label);
      if (s.type !== 'llm' && !s.query.trim()) return { error: `Source "${label}" needs a query.` };
      if (s.type === 'llm' && !s.instructions.trim())
        return { error: `Source "${label}" needs instructions.` };
      if (s.type === 'gcp_logs' && s.logSince.trim() && !LOOKBACK.test(s.logSince.trim()))
        return { error: `Source "${label}": lookback must look like 30m, 2h or 1d.` };
    }
  }
  let reportSpec: MonitorInput['reportSpec'] = null;
  if (f.runMode === 'report') {
    if (!f.reportSpecText.trim()) return { error: 'Report mode needs a report spec (JSON).' };
    try {
      reportSpec = JSON.parse(f.reportSpecText);
    } catch (e) {
      return { error: `Report spec is not valid JSON: ${(e as Error).message}` };
    }
    if (!reportSpec || typeof reportSpec !== 'object' || !Array.isArray(reportSpec.rows))
      return { error: 'Report spec must be an object with a "rows" array.' };
  }
  const maxToolCalls = f.maxToolCalls.trim() === '' ? null : Number(f.maxToolCalls);
  if (maxToolCalls !== null && (!Number.isInteger(maxToolCalls) || maxToolCalls < 0))
    return { error: 'Max tool calls must be a whole number.' };
  return {
    body: {
      tenantId: f.scope === 'tenant' ? f.tenantId : null,
      systemId: f.scope === 'system' ? f.systemId : null,
      name: f.name.trim(),
      description: f.description.trim() || null,
      cronExpression: f.cronExpression.trim(),
      enabled: f.enabled,
      ...(f.fromSavedResponse ? {} : { runMode: f.runMode }),
      instructions: f.instructions.trim(),
      maxToolCalls,
      combineMode: f.combineMode,
      gateInstructions: f.combineMode === 'llm_gate' ? f.gateInstructions.trim() || null : null,
      logSince: f.logSince.trim() || null,
      reportSpec,
      notifyEmails: textToList(f.notifyEmails),
      notifyMode: f.notifyMode,
      ...(f.fromSavedResponse
        ? {}
        : {
            sources: f.sources.map((s) => ({
              label: s.label.trim(),
              type: s.type,
              query: s.type === 'llm' ? '' : s.query.trim(),
              logSince: s.type === 'gcp_logs' ? s.logSince.trim() || null : null,
              entityKeyColumns: textToList(s.entityKeyColumns),
              instructions: s.type === 'llm' ? s.instructions.trim() || null : null,
            })),
          }),
    },
  };
}

export interface AlertFinding {
  title?: string;
  severity?: string;
  finding?: string;
  summary?: string;
  actions: MonitorProposedAction[];
  content?: MonitorProposalItem['content'];
}

/** An alert's proposal is either a whole run proposal (`{summary, items}`) or its one item. */
export function alertFinding(alert: Pick<MonitorAlert, 'proposal' | 'entityKey'>): AlertFinding {
  const p = alert.proposal as (MonitorProposal & Partial<MonitorProposalItem>) | null | undefined;
  if (!p) return { actions: [] };
  if (Array.isArray(p.items)) {
    const item = p.items.find((i) => i.entityKey === alert.entityKey) ?? p.items[0];
    return {
      title: item?.title,
      severity: item?.severity ?? p.severity,
      finding: item?.finding,
      summary: p.summary,
      actions: item?.proposedActions ?? [],
      content: item?.content,
    };
  }
  return {
    title: p.title,
    severity: p.severity,
    finding: p.finding,
    summary: p.summary,
    actions: p.proposedActions ?? [],
    content: p.content,
  };
}

/** Systems a monitor may run on: every one (global), the tenant's, or its own. */
export const systemsFor = <S extends { id: string; tenantId: string }>(
  m: { scope: string; tenantId?: string | null; systemId?: string | null },
  systems: S[]
) =>
  systems.filter((s) =>
    m.scope === 'global' ? true : m.scope === 'tenant' ? s.tenantId === m.tenantId : s.id === m.systemId
  );
